import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { AuthImage } from "@/components/AuthImage";
import { READ_VOICES, SPEEDS, VoicePreview, SpeedSelect } from "@/components/VoiceControls";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft, Moon, Play, Pause, SkipBack, SkipForward, Plus, X, ArrowUp, ArrowDown, Loader2,
} from "lucide-react";
import { toast } from "sonner";

const QUEUE_KEY = "cml_bedtime_queue";

export default function Bedtime() {
  const [stories, setStories] = useState([]);
  const [queue, setQueue] = useState(() => JSON.parse(localStorage.getItem(QUEUE_KEY) || "[]"));
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [loadingAudio, setLoadingAudio] = useState(false);
  const [voice, setVoice] = useState(localStorage.getItem("cml_read_voice") || "shimmer");
  const [speed, setSpeed] = useState(Number(localStorage.getItem("cml_read_speed")) || 1);
  const [autoNext, setAutoNext] = useState(localStorage.getItem("cml_bedtime_auto") !== "0");
  const audioRef = useRef(null);
  const gapRef = useRef(null);

  useEffect(() => {
    api.get("/stories").then(({ data }) => setStories(data)).catch(() => {});
    return () => {
      if (audioRef.current) audioRef.current.pause();
      clearTimeout(gapRef.current);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
  }, [queue]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = speed;
    localStorage.setItem("cml_read_speed", String(speed));
  }, [speed]);

  const byId = (id) => stories.find((s) => s.story_id === id);
  const queued = queue.map(byId).filter(Boolean);
  const current = queued[index];

  const stop = () => {
    clearTimeout(gapRef.current);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlaying(false);
  };

  const playAt = async (i) => {
    const story = queued[i];
    if (!story) return stop();
    stop();
    setIndex(i);
    setLoadingAudio(true);
    try {
      const { data } = await api.post(`/stories/${story.story_id}/narrate`, { voice, language: null });
      const file = await api.get(`/creations/${data.creation_id}/file`, { responseType: "blob" });
      const audio = new Audio(URL.createObjectURL(file.data));
      audio.playbackRate = speed;
      audio.onended = () => {
        setPlaying(false);
        audioRef.current = null;
        if (autoNext && i + 1 < queued.length) gapRef.current = setTimeout(() => playAt(i + 1), 2000);
      };
      audioRef.current = audio;
      await audio.play();
      setPlaying(true);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not read that story");
    } finally {
      setLoadingAudio(false);
    }
  };

  const toggle = () => {
    if (!queued.length) return toast.info("Add a story to the queue first");
    if (playing) {
      audioRef.current?.pause();
      setPlaying(false);
      return;
    }
    if (audioRef.current) {
      audioRef.current.play();
      setPlaying(true);
      return;
    }
    playAt(index);
  };

  const move = (i, dir) => {
    setQueue((q) => {
      const next = [...q];
      const j = i + dir;
      if (j < 0 || j >= next.length) return q;
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  const add = (id) => {
    if (queue.includes(id)) return;
    setQueue((q) => [...q, id]);
  };

  const remove = (id) => {
    stop();
    setQueue((q) => q.filter((x) => x !== id));
    setIndex(0);
  };

  return (
    <div className="mx-auto max-w-6xl px-5 py-12 sm:px-8" data-testid="bedtime-page">
      <Link to="/stories" data-testid="bedtime-back" className="inline-flex items-center gap-2 text-sm text-ink-soft hover:text-brand">
        <ArrowLeft className="h-4 w-4 rtl:rotate-180" /> Story Library
      </Link>

      <div className="mt-6 flex items-start gap-4">
        <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand/15 text-brand">
          <Moon className="h-6 w-6" />
        </span>
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tighter sm:text-4xl">Bedtime Playlist</h1>
          <p className="mt-2 max-w-2xl text-sm text-ink-soft md:text-base">
            Queue up a few stories and let them play one after another, read out loud.
          </p>
        </div>
      </div>

      <div className="mt-8 rounded-3xl border border-white/10 bg-panel p-5 sm:p-6" data-testid="bedtime-player">
        <div className="flex flex-wrap items-center gap-5">
          <div className="h-20 w-28 shrink-0 overflow-hidden rounded-2xl bg-page">
            {current?.cover_creation_id ? (
              <AuthImage
                creationId={current.cover_creation_id}
                alt={current.title}
                className="h-full w-full object-cover"
                testid="bedtime-current-cover"
              />
            ) : (
              <span className="flex h-full items-center justify-center text-xs text-ink-soft">—</span>
            )}
          </div>

          <div className="min-w-[180px] flex-1">
            <p data-testid="bedtime-progress" className="text-xs uppercase tracking-widest text-ink-soft">
              {queued.length ? `Story ${index + 1} of ${queued.length}` : "Nothing queued"}
            </p>
            <p dir="auto" data-testid="bedtime-current-title" className="mt-1 truncate font-display text-base font-bold tracking-tight">
              {current?.title || "Pick a story below"}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              data-testid="bedtime-prev"
              onClick={() => playAt(Math.max(0, index - 1))}
              disabled={!queued.length || index === 0}
              className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-40"
            >
              <SkipBack className="h-4 w-4" />
            </button>
            <button
              data-testid="bedtime-play"
              onClick={toggle}
              className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-brand text-white hover:bg-brand-hover"
            >
              {loadingAudio ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : playing ? (
                <Pause className="h-5 w-5" />
              ) : (
                <Play className="h-5 w-5" />
              )}
            </button>
            <button
              data-testid="bedtime-next"
              onClick={() => playAt(index + 1)}
              disabled={index + 1 >= queued.length}
              className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-40"
            >
              <SkipForward className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-white/10 pt-5">
          <Select
            value={voice}
            onValueChange={(v) => {
              setVoice(v);
              localStorage.setItem("cml_read_voice", v);
              stop();
            }}
          >
            <SelectTrigger data-testid="bedtime-voice" className="h-9 w-[150px] rounded-full text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {READ_VOICES.map((v) => (
                <SelectItem key={v.id} value={v.id} data-testid={`bedtime-voice-${v.id}`}>
                  {v.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <VoicePreview voice={voice} testid="bedtime-voice-preview" />
          <SpeedSelect value={speed} onChange={setSpeed} testid="bedtime-speed" />
          <label className="ms-auto flex items-center gap-2 text-xs text-ink-soft">
            Auto-play next
            <Switch
              data-testid="bedtime-autonext"
              checked={autoNext}
              onCheckedChange={(v) => {
                setAutoNext(v);
                localStorage.setItem("cml_bedtime_auto", v ? "1" : "0");
              }}
            />
          </label>
        </div>
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-base font-bold tracking-tight text-ink-soft md:text-lg">Queue</h2>
          {queued.length === 0 && (
            <p data-testid="bedtime-queue-empty" className="mt-3 rounded-2xl border border-white/10 bg-panel p-5 text-sm text-ink-soft">
              Nothing queued yet — add stories from the right.
            </p>
          )}
          <ul className="mt-3 space-y-2" data-testid="bedtime-queue">
            {queued.map((s, i) => (
              <li
                key={s.story_id}
                data-testid={`bedtime-queue-item-${s.story_id}`}
                className={`flex items-center gap-3 rounded-2xl border p-3 ${
                  i === index ? "border-brand/50 bg-brand/10" : "border-white/10 bg-panel"
                }`}
              >
                <span className="w-6 text-center text-xs text-ink-soft">{i + 1}</span>
                <button
                  onClick={() => playAt(i)}
                  dir="auto"
                  data-testid={`bedtime-play-${s.story_id}`}
                  className="min-w-0 flex-1 truncate text-start text-sm hover:text-brand"
                >
                  {s.title}
                </button>
                <button onClick={() => move(i, -1)} data-testid={`bedtime-up-${s.story_id}`} className="text-ink-soft hover:text-brand">
                  <ArrowUp className="h-4 w-4" />
                </button>
                <button onClick={() => move(i, 1)} data-testid={`bedtime-down-${s.story_id}`} className="text-ink-soft hover:text-brand">
                  <ArrowDown className="h-4 w-4" />
                </button>
                <button onClick={() => remove(s.story_id)} data-testid={`bedtime-remove-${s.story_id}`} className="text-ink-soft hover:text-destructive">
                  <X className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h2 className="text-base font-bold tracking-tight text-ink-soft md:text-lg">Your stories</h2>
          {stories.length === 0 && (
            <div className="mt-3 rounded-2xl border border-white/10 bg-panel p-5 text-sm text-ink-soft">
              No stories yet.{" "}
              <Button asChild variant="link" className="h-auto p-0 text-brand">
                <Link to="/chat?persona=storyteller" data-testid="bedtime-go-storyteller">Write one</Link>
              </Button>
            </div>
          )}
          <ul className="mt-3 space-y-2" data-testid="bedtime-library">
            {stories.map((s) => (
              <li
                key={s.story_id}
                className="flex items-center gap-3 rounded-2xl border border-white/10 bg-panel p-3"
              >
                <span dir="auto" className="min-w-0 flex-1 truncate text-sm">{s.title}</span>
                <button
                  data-testid={`bedtime-add-${s.story_id}`}
                  onClick={() => add(s.story_id)}
                  disabled={queue.includes(s.story_id)}
                  className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 text-xs hover:bg-white/20 disabled:opacity-40"
                >
                  <Plus className="h-3.5 w-3.5" /> Queue
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
